import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../contexts/AdminContext';
import { useApp } from '../contexts/AppContext';
import { supabase } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiSettings, FiUsers, FiMessageSquare, FiBarChart3, FiPlus, FiEdit3, 
  FiTrash2, FiEye, FiEyeOff, FiDownload, FiUpload, FiSave, FiX, 
  FiCheck, FiAlertCircle, FiRefreshCw, FiBookOpen, FiFilter
} = FiIcons;

const AdminPanel = () => {
  const { user } = useAuth();
  const { isAdmin, adminData, addQuote, updateQuote, deleteQuote, toggleQuoteStatus, updateAppSettings, exportData, importData } = useAdmin();
  const { addNotification } = useApp();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showAddQuoteModal, setShowAddQuoteModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importDataState, setImportDataState] = useState('');
  const [loading, setLoading] = useState(false);

  // New state for Supabase quotes management
  const [categories, setCategories] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Quote form state
  const [quoteForm, setQuoteForm] = useState({
    text: '',
    author: '',
    category_id: '',
    tags: [],
    difficulty_level: 'beginner',
    is_featured: false
  });

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({});

  useEffect(() => {
    if (adminData.settings) {
      setSettingsForm(adminData.settings);
    }
  }, [adminData.settings]);

  // Load Supabase data when "quotes-db" tab is active
  useEffect(() => {
    if (activeTab === 'quotes-db') {
      loadSupabaseData();
    }
  }, [activeTab]);

  const loadSupabaseData = async () => {
    setLoading(true);
    try {
      // Load categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('quotes_categories_ilm2024')
        .select('*')
        .order('sort_order');

      if (categoriesError) throw categoriesError;

      // Load quotes with category information
      const { data: quotesData, error: quotesError } = await supabase
        .from('quotes_collection_ilm2024')
        .select(`
          *,
          category:quotes_categories_ilm2024(*)
        `)
        .order('created_at', { ascending: false });

      if (quotesError) throw quotesError;

      setCategories(categoriesData || []);
      setQuotes(quotesData || []);
    } catch (error) {
      console.error('Error loading Supabase data:', error);
      addNotification({
        type: 'error',
        title: 'Loading Error',
        message: 'Failed to load quotes data from database.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSupabaseQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteForm.text || !quoteForm.author || !quoteForm.category_id) {
      addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please fill in all required fields.'
      });
      return;
    }

    setLoading(true);
    try {
      const quoteData = {
        text: quoteForm.text,
        author: quoteForm.author,
        category_id: quoteForm.category_id,
        tags: quoteForm.tags,
        difficulty_level: quoteForm.difficulty_level,
        is_featured: quoteForm.is_featured,
        is_active: true,
        created_by: user?.id
      };

      if (editingQuote) {
        // Update existing quote
        const { error } = await supabase
          .from('quotes_collection_ilm2024')
          .update(quoteData)
          .eq('id', editingQuote.id);

        if (error) throw error;

        addNotification({
          type: 'success',
          title: 'Quote Updated',
          message: 'Quote has been updated successfully!'
        });
      } else {
        // Insert new quote
        const { error } = await supabase
          .from('quotes_collection_ilm2024')
          .insert([quoteData]);

        if (error) throw error;

        addNotification({
          type: 'success',
          title: 'Quote Added',
          message: 'New quote has been added successfully!'
        });
      }

      // Reset form and reload data
      setQuoteForm({
        text: '',
        author: '',
        category_id: '',
        tags: [],
        difficulty_level: 'beginner',
        is_featured: false
      });
      setEditingQuote(null);
      setShowAddQuoteModal(false);
      loadSupabaseData();

    } catch (error) {
      console.error('Error saving quote:', error);
      addNotification({
        type: 'error',
        title: 'Save Error',
        message: 'Failed to save quote. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSupabaseQuote = (quote) => {
    setQuoteForm({
      text: quote.text,
      author: quote.author,
      category_id: quote.category_id,
      tags: quote.tags || [],
      difficulty_level: quote.difficulty_level || 'beginner',
      is_featured: quote.is_featured || false
    });
    setEditingQuote(quote);
    setShowAddQuoteModal(true);
  };

  const handleDeleteSupabaseQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('quotes_collection_ilm2024')
        .delete()
        .eq('id', quoteId);

      if (error) throw error;

      addNotification({
        type: 'success',
        title: 'Quote Deleted',
        message: 'Quote has been deleted successfully!'
      });

      loadSupabaseData();
    } catch (error) {
      console.error('Error deleting quote:', error);
      addNotification({
        type: 'error',
        title: 'Delete Error',
        message: 'Failed to delete quote. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleQuoteStatus = async (quoteId, currentStatus) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('quotes_collection_ilm2024')
        .update({ is_active: !currentStatus })
        .eq('id', quoteId);

      if (error) throw error;

      addNotification({
        type: 'success',
        title: 'Status Updated',
        message: 'Quote status has been updated!'
      });

      loadSupabaseData();
    } catch (error) {
      console.error('Error updating quote status:', error);
      addNotification({
        type: 'error',
        title: 'Update Error',
        message: 'Failed to update quote status.'
      });
    } finally {
      setLoading(false);
    }
  };

  const getFilteredQuotes = () => {
    let filtered = [...quotes];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(quote => quote.category?.name === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(quote =>
        quote.text.toLowerCase().includes(query) ||
        quote.author.toLowerCase().includes(query) ||
        quote.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    return filtered;
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: FiBarChart3 },
    { id: 'quotes-db', name: 'Quotes Database', icon: FiBookOpen },
    { id: 'quotes', name: 'Legacy Quotes', icon: FiMessageSquare },
    { id: 'users', name: 'Users', icon: FiUsers },
    { id: 'settings', name: 'App Settings', icon: FiSettings }
  ];

  const quoteCategories = [
    { value: 'education', label: 'Education' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'inspiration', label: 'Inspiration' },
    { value: 'motivation', label: 'Motivation' }
  ];

  // Legacy quote handling functions (keeping existing functionality)
  const handleQuoteSubmit = (e) => {
    e.preventDefault();
    if (!quoteForm.text || !quoteForm.author) {
      addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Please fill in all required fields.'
      });
      return;
    }

    if (editingQuote) {
      updateQuote(editingQuote.id, quoteForm);
      setEditingQuote(null);
    } else {
      addQuote(quoteForm);
    }

    setQuoteForm({
      text: '',
      author: '',
      category: 'education'
    });
    setShowAddQuoteModal(false);
  };

  const handleEditQuote = (quote) => {
    setQuoteForm({
      text: quote.text,
      author: quote.author,
      category: quote.category
    });
    setEditingQuote(quote);
    setShowAddQuoteModal(true);
  };

  const handleDeleteQuote = (quoteId) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      deleteQuote(quoteId);
    }
  };

  const handleBulkAction = (action) => {
    if (selectedQuotes.length === 0) {
      addNotification({
        type: 'warning',
        title: 'No Selection',
        message: 'Please select quotes to perform bulk actions.'
      });
      return;
    }

    switch (action) {
      case 'activate':
        selectedQuotes.forEach(id => {
          const quote = adminData.quotes.find(q => q.id === id);
          if (quote && !quote.isActive) {
            toggleQuoteStatus(id);
          }
        });
        break;
      case 'deactivate':
        selectedQuotes.forEach(id => {
          const quote = adminData.quotes.find(q => q.id === id);
          if (quote && quote.isActive) {
            toggleQuoteStatus(id);
          }
        });
        break;
      case 'delete':
        if (window.confirm(`Are you sure you want to delete ${selectedQuotes.length} quotes?`)) {
          selectedQuotes.forEach(id => deleteQuote(id));
        }
        break;
    }
    setSelectedQuotes([]);
  };

  const handleSettingsSave = () => {
    updateAppSettings(settingsForm);
  };

  const handleImport = () => {
    try {
      importData('quotes', importDataState);
      setImportModalOpen(false);
      setImportDataState('');
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Import Failed',
        message: 'Please check your JSON format and try again.'
      });
    }
  };

  return (
    <div className="space-y-6 pb-16 md:pb-0">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiSettings} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Admin Panel</h1>
          <p className="text-gray-600">Manage your ILM Global application</p>
          <p className="text-green-600 mt-2">
            <SafeIcon icon={FiCheck} className="w-4 h-4 inline mr-1" />
            Logged in as admin: {user?.email || "admin@ilmglobal.org"}
          </p>
        </div>
      </motion.div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-x-auto">
        <div className="flex space-x-1 p-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-5 h-5" />
              <span>{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-800">{adminData.analytics?.totalUsers || 0}</p>
              </div>
              <SafeIcon icon={FiUsers} className="w-8 h-8 text-blue-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Database Quotes</p>
                <p className="text-2xl font-bold text-gray-800">{quotes.length}</p>
              </div>
              <SafeIcon icon={FiBookOpen} className="w-8 h-8 text-green-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Legacy Quotes</p>
                <p className="text-2xl font-bold text-gray-800">
                  {adminData.quotes?.filter(q => q.isActive).length || 0}
                </p>
              </div>
              <SafeIcon icon={FiMessageSquare} className="w-8 h-8 text-purple-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Categories</p>
                <p className="text-2xl font-bold text-gray-800">{categories.length}</p>
              </div>
              <SafeIcon icon={FiBarChart3} className="w-8 h-8 text-orange-500" />
            </div>
          </motion.div>
        </div>
      )}

      {/* Quotes Database Tab */}
      {activeTab === 'quotes-db' && (
        <div className="space-y-6">
          {/* Actions Bar */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
              <h2 className="text-xl font-bold text-gray-800">Quotes Database Management</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setEditingQuote(null);
                    setQuoteForm({
                      text: '',
                      author: '',
                      category_id: '',
                      tags: [],
                      difficulty_level: 'beginner',
                      is_featured: false
                    });
                    setShowAddQuoteModal(true);
                  }}
                  className="flex items-center space-x-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  <span>Add Quote</span>
                </button>
                <button
                  onClick={loadSupabaseData}
                  className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  disabled={loading}
                >
                  <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search quotes, authors, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Quotes List */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <SafeIcon icon={FiRefreshCw} className="w-8 h-8 mx-auto mb-4 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading quotes...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quote
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Author
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredQuotes().map((quote) => (
                      <tr key={quote.id}>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {quote.text}
                          </div>
                          {quote.tags && quote.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {quote.tags.slice(0, 3).map((tag, index) => (
                                <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{quote.author}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {quote.category?.name || 'No Category'}
                          </span>
                          {quote.difficulty_level && (
                            <div className="mt-1">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                quote.difficulty_level === 'beginner' ? 'bg-green-100 text-green-700' :
                                quote.difficulty_level === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {quote.difficulty_level}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleQuoteStatus(quote.id, quote.is_active)}
                            className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                              quote.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <SafeIcon icon={quote.is_active ? FiEye : FiEyeOff} className="w-3 h-3 mr-1" />
                            {quote.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditSupabaseQuote(quote)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSupabaseQuote(quote.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {getFilteredQuotes().length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    No quotes found matching your criteria.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legacy Quotes Management Tab - Keep existing functionality */}
      {activeTab === 'quotes' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
              <h2 className="text-xl font-bold text-gray-800">Legacy Quotes Management</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowAddQuoteModal(true)}
                  className="flex items-center space-x-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  <span>Add Quote</span>
                </button>
                <button
                  onClick={() => exportData('quotes')}
                  className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                >
                  <SafeIcon icon={FiDownload} className="w-4 h-4" />
                  <span>Export</span>
                </button>
                <button
                  onClick={() => setImportModalOpen(true)}
                  className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <SafeIcon icon={FiUpload} className="w-4 h-4" />
                  <span>Import</span>
                </button>
              </div>
            </div>

            {/* Bulk Actions */}
            {selectedQuotes.length > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {selectedQuotes.length} quote(s) selected
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleBulkAction('activate')}
                    className="text-xs bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                  >
                    Activate
                  </button>
                  <button
                    onClick={() => handleBulkAction('deactivate')}
                    className="text-xs bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                  >
                    Deactivate
                  </button>
                  <button
                    onClick={() => handleBulkAction('delete')}
                    className="text-xs bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Legacy Quotes List */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedQuotes(adminData.quotes.map(q => q.id));
                          } else {
                            setSelectedQuotes([]);
                          }
                        }}
                        checked={selectedQuotes.length === adminData.quotes?.length}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quote
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Author
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(adminData.quotes || []).map((quote) => (
                    <tr key={quote.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedQuotes.includes(quote.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedQuotes([...selectedQuotes, quote.id]);
                            } else {
                              setSelectedQuotes(selectedQuotes.filter(id => id !== quote.id));
                            }
                          }}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {quote.text}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{quote.author}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {quote.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleQuoteStatus(quote.id)}
                          className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                            quote.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <SafeIcon icon={quote.isActive ? FiEye : FiEyeOff} className="w-3 h-3 mr-1" />
                          {quote.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditQuote(quote)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuote(quote.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* App Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-xl font-bold text-gray-800 mb-6">App Settings</h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Name
              </label>
              <input
                type="text"
                value={settingsForm.appName || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, appName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Maintenance Mode</h3>
                <p className="text-xs text-gray-500">Enable to prevent user access during updates</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsForm.maintenanceMode || false}
                  onChange={(e) => setSettingsForm({ ...settingsForm, maintenanceMode: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Registration Enabled</h3>
                <p className="text-xs text-gray-500">Allow new users to register</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsForm.registrationEnabled || false}
                  onChange={(e) => setSettingsForm({ ...settingsForm, registrationEnabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Points Per Quiz
              </label>
              <input
                type="number"
                value={settingsForm.pointsPerQuiz || 20}
                onChange={(e) => setSettingsForm({ ...settingsForm, pointsPerQuiz: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <button
              onClick={handleSettingsSave}
              className="flex items-center space-x-2 bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-5 h-5" />
              <span>Save Settings</span>
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Quote Modal */}
      <AnimatePresence>
        {showAddQuoteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddQuoteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingQuote ? 'Edit Quote' : 'Add New Quote'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddQuoteModal(false);
                    setEditingQuote(null);
                    setQuoteForm({
                      text: '',
                      author: '',
                      category_id: '',
                      tags: [],
                      difficulty_level: 'beginner',
                      is_featured: false
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={activeTab === 'quotes-db' ? handleSupabaseQuoteSubmit : handleQuoteSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quote Text *
                  </label>
                  <textarea
                    value={quoteForm.text}
                    onChange={(e) => setQuoteForm({ ...quoteForm, text: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows="3"
                    placeholder="Enter the quote text..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author *
                  </label>
                  <input
                    type="text"
                    value={quoteForm.author}
                    onChange={(e) => setQuoteForm({ ...quoteForm, author: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter the author name..."
                    required
                  />
                </div>

                {activeTab === 'quotes-db' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        value={quoteForm.category_id}
                        onChange={(e) => setQuoteForm({ ...quoteForm, category_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      >
                        <option value="">Select a category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tags (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={quoteForm.tags.join(', ')}
                        onChange={(e) => setQuoteForm({ 
                          ...quoteForm, 
                          tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag) 
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="motivation, inspiration, success..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Difficulty Level
                      </label>
                      <select
                        value={quoteForm.difficulty_level}
                        onChange={(e) => setQuoteForm({ ...quoteForm, difficulty_level: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_featured"
                        checked={quoteForm.is_featured}
                        onChange={(e) => setQuoteForm({ ...quoteForm, is_featured: e.target.checked })}
                        className="mr-2"
                      />
                      <label htmlFor="is_featured" className="text-sm font-medium text-gray-700">
                        Featured Quote
                      </label>
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={quoteForm.category}
                      onChange={(e) => setQuoteForm({ ...quoteForm, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {quoteCategories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddQuoteModal(false);
                      setEditingQuote(null);
                      setQuoteForm({
                        text: '',
                        author: '',
                        category_id: '',
                        tags: [],
                        difficulty_level: 'beginner',
                        is_featured: false
                      });
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center space-x-2 bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>{loading ? 'Saving...' : (editingQuote ? 'Update' : 'Add')} Quote</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Import Modal */}
      <AnimatePresence>
        {importModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setImportModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">Import Quotes</h2>
                <button
                  onClick={() => setImportModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    JSON Data
                  </label>
                  <textarea
                    value={importDataState}
                    onChange={(e) => setImportDataState(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows="10"
                    placeholder="Paste your JSON data here..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setImportModalOpen(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImport}
                    className="flex items-center space-x-2 bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <SafeIcon icon={FiUpload} className="w-4 h-4" />
                    <span>Import</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;